import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, Sprout, Sparkles } from 'lucide-react';
import LoadingAnimation from './LoadingAnimation';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GIRAFFE_MASCOT = '/images/simby-mascote.png';

// ──────────────────────────────────────────────────────────────────────────
// EmotionDiaryInsights — "O que a Simby vem te dizendo"
// Lê os insights do Diário de Emoções (briefing v2 — espelho gentil).
// Mostra o último em destaque + lista dos anteriores.
// Lê de /api/emotion-checkin/insights — NÃO depende mais do legacy v2 TCC.
// ──────────────────────────────────────────────────────────────────────────
const EmotionDiaryInsights = () => {
  const navigate = useNavigate();
  const [insights, setInsights] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/emotion-checkin/insights?limit=20`, { withCredentials: true })
      .then(res => {
        if (cancelled) return;
        setInsights(res.data?.insights || []);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Error loading insights:', err);
        setError('Não consegui carregar seus insights agora. Tente em alguns minutos.');
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <LoadingAnimation size="md" />
      </div>
    );
  }

  const [latest, ...older] = insights;

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Voltar */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <img
            src={GIRAFFE_MASCOT}
            alt="Simby"
            className="w-12 h-16 object-contain flex-shrink-0"
          />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              O que a Simby vem te dizendo
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Pequenos espelhos do que apareceu nos seus registros.
            </p>
          </div>
        </div>

        {/* Erro */}
        {error && (
          <Card className="p-4 mb-4 bg-red-50 border-red-200">
            <p className="text-sm text-red-700">{error}</p>
          </Card>
        )}

        {/* Vazio */}
        {!error && insights.length === 0 && (
          <Card className="p-8 text-center bg-purple-50 border-purple-100">
            <Sprout className="w-10 h-10 text-purple-400 mx-auto mb-3" />
            <h2 className="font-bold text-gray-900 mb-2">
              Ainda não há insights por aqui
            </h2>
            <p className="text-sm text-gray-600 max-w-md mx-auto mb-5">
              A Simby gera um espelho a cada check-in concluído. Faça seu primeiro
              registro do dia para receber o seu.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Ir para o check-in
            </Button>
          </Card>
        )}

        {/* Insight mais recente em destaque */}
        {latest && (
          <Card className="p-6 mb-5 bg-white border-2 border-purple-300 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <Sparkles className="w-5 h-5 text-purple-500 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-purple-700 uppercase tracking-widest mb-2">
                  O espelho de hoje
                </p>
                <p className="text-gray-900 text-base leading-relaxed whitespace-pre-line">
                  {latest.texto}
                </p>
                <p className="text-xs text-gray-400 mt-3">
                  {formatDate(latest.created_at)}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Lista de anteriores */}
        {older.length > 0 && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-6 mb-2">
              Anteriores
            </p>
            {older.map((it) => (
              <Card key={it.einsight_id} className="p-4 bg-white border border-gray-200">
                <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                  {it.texto}
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  {formatDate(it.created_at)}
                </p>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────────
const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

export default EmotionDiaryInsights;
